import {
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  SecurityContext,
  signal,
} from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";

import { Subscription } from "rxjs";

import { CountriesApiService } from "@components/shared/countries/api";
import { Country } from "@components/shared/countries/models";
import { InputSelectOption } from "@components/shared/inputs/input-select/input-select.model";
import { RiskLevel } from "@components/shared/risk-level-sets/models";
import { Tag } from "@components/shared/tags/models";
import { TextConstants } from "@shared/constants";
import {
  IConnectionExtended,
  IDeliveryExtended,
  ILocationExtended,
  ILocationType,
  IMaterialExtended,
  IOrganisation,
  IProductExtended,
} from "@shared/interfaces";
import {
  AuthenticationService,
  CommonService,
  ConnectionsService,
  DeliveriesService,
  LocationsService,
  MaterialsService,
  NotificationService,
  ProductsService,
} from "@shared/services";

import {
  RiskAssessmentReportIndicatorsApiService,
  RiskAssessmentReportsApiService,
} from "../../api";
import { RiskAssessmentReport, RiskAssessmentReportIndicator } from "../../models";
import { RiskAssessmentReportIndicatorMitigationsMapService } from "../../services";

interface GraphQLService<T> {
  getByIdsGraphQL: (ids: string[], pagination?: unknown, includes?: string[]) => Promise<T[]>;
}

type RecordApiServices = {
  countries: { get: (countryId: string) => Promise<Country> };
  connections: { get: (connectionId: string) => Promise<IConnectionExtended> };
  locations: { get: (locationId: string) => Promise<ILocationExtended> };
  materials: { get: (materialId: string) => Promise<IMaterialExtended> };
  products: { get: (productId: string) => Promise<IProductExtended> };
  deliveries: { get: (deliveryId: string) => Promise<IDeliveryExtended> };
};

type RecordType =
  | Country
  | IConnectionExtended
  | ILocationExtended
  | IMaterialExtended
  | IProductExtended
  | IDeliveryExtended;

@Component({
  standalone: false,
  selector: "app-risk-assessment-report-print-view",
  templateUrl: "./risk-assessment-report-print-view.component.html",
  styleUrls: ["./risk-assessment-report-print-view.component.scss"],
})
export class RiskAssessmentReportPrintViewComponent implements OnInit, OnDestroy {
  isLoading = signal(true);

  report = signal<RiskAssessmentReport>(undefined);

  riskLevels = signal<RiskLevel[]>([]);

  indicatorGroups = signal<RiskAssessmentReportIndicator[]>([]);

  recordNameOrId = signal<string>(undefined);

  readonly printSectionId = "risk-assessment-report-print-section";

  readonly dateFormat = signal("yyyy-MM-dd");

  readonly reportIndicatorMitigationsMapService = inject(
    RiskAssessmentReportIndicatorMitigationsMapService,
  );

  private readonly commonService = inject(CommonService);

  note = computed(() => this.getSafeHtml(this.report().note || "-"));

  readonly printStyle: any = {
    body: { overflow: "visible !important" },
    "@page": {
      size: "A4",
      margin: "10mm",
    },
    ".print-button": {
      display: "none !important",
    },
    "@media print": {
      "body, html": {
        height: "auto !important",
        overflow: "visible !important",
      },
    },
  };

  recordDetails = signal<{
    organisation?: IOrganisation;
    location?: ILocationExtended;
    product?: IProductExtended;
    material?: IMaterialExtended;
    country?: Country;
    delivery?: IDeliveryExtended;
  }>({});

  readonly translations = {
    recordDetails: TextConstants.RECORD_DETAILS,
    product: TextConstants.PRODUCT,
    material: TextConstants.MATERIAL,
    country: TextConstants.COUNTRY,
    delivery: TextConstants.DELIVERY,
    completedDate: TextConstants.DATE_COMPLETED,
    validUntil: TextConstants.VALID_UNTIL,
    note: TextConstants.NOTE,
    calculatedRisk: TextConstants.CALCULATED_RISK,
    residualRisk: TextConstants.RESIDUAL_RISK,
    location: TextConstants.LOCATION,
    organisation: TextConstants.ORGANISATION,
    types: $localize`:@@shared.types:Type(s)`,
    name: TextConstants.NAME,
    website: TextConstants.WEBSITE,
    address: TextConstants.ADDRESS,
    zipCode: TextConstants.ZIP_POSTCODE,
    region: TextConstants.STATE_REGION,
  };

  private readonly authenticationService = inject(AuthenticationService);

  private readonly route = inject(ActivatedRoute);

  private readonly reportsApi = inject(RiskAssessmentReportsApiService);

  private readonly reportIndicatorsApi = inject(RiskAssessmentReportIndicatorsApiService);

  private readonly countriesApi = inject(CountriesApiService);

  private readonly connectionsApi = inject(ConnectionsService);

  private readonly locationsApi = inject(LocationsService);

  private readonly materialsApi = inject(MaterialsService);

  private readonly productsApi = inject(ProductsService);

  private readonly sanitizer = inject(DomSanitizer);

  private readonly deliveriesApi = inject(DeliveriesService);

  private readonly subscriptions = new Subscription();

  private readonly notificationService = inject(NotificationService);

  readonly activeOrganisation = this.authenticationService.getActiveOrganisation();

  private readonly getConnection = this.createRecordGetter<IConnectionExtended>(
    this.connectionsApi,
    ["CUSTOM_FIELDS", "FULL_ADDRESS"],
  );

  private readonly getLocation = this.createRecordGetter<ILocationExtended>(this.locationsApi, [
    "TYPES",
    "CUSTOM_FIELDS",
    "FULL_ADDRESS",
  ]);

  private readonly getProduct = this.createRecordGetter<IProductExtended>(this.productsApi);

  private readonly getMaterial = this.createRecordGetter<IMaterialExtended>(this.materialsApi);

  private readonly getCountry = this.createRecordGetter<Country>(this.countriesApi, []);

  private readonly getDelivery = this.createRecordGetter<IDeliveryExtended>(this.deliveriesApi, [
    "CUSTOM_FIELDS",
    "AGENTS",
  ]);

  private readonly mapRecordTypeToApiService: RecordApiServices = {
    countries: this.getCountry,
    connections: this.getConnection,
    locations: this.getLocation,
    materials: this.getMaterial,
    products: this.getProduct,
    deliveries: this.getDelivery,
  };

  private readonly mapRecordTypeToRecordTypeStandard = {
    countries: "country",
    connections: "organisation",
    locations: "location",
    materials: "material",
    products: "product",
    deliveries: "delivery",
  };

  private readonly recordTypeToNameProperty: Record<string, (record: RecordType) => string> = {
    countries: (record: Country) => record.name,
    connections: (record: IConnectionExtended) => record.name,
    locations: (record: ILocationExtended) => record.name,
    materials: (record: IMaterialExtended) => record.name,
    products: (record: IProductExtended) => record.name,
    deliveries: (record: IDeliveryExtended) => record.deliveryId,
  } as const;

  constructor() {}

  async ngOnInit() {
    this.isLoading.set(true);

    const id = this.route.snapshot.params["id"];

    try {
      const [report, riskLevels, indicatorGroups] = await Promise.all([
        this.reportsApi.get(id),
        this.reportsApi.getRiskLevels(id),
        this.reportIndicatorsApi.getAll(id),
      ]);

      await Promise.all([
        this.reportIndicatorMitigationsMapService.setMap(id, indicatorGroups),
        this.setRecordDetails(report.recordUris[0]),
      ]);

      this.report.set(report);
      this.riskLevels.set(riskLevels);
      this.indicatorGroups.set(indicatorGroups);
      this.isLoading.set(false);
    } catch (error) {
      this.notificationService.showError(error);
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  getCountryName(code: string): string {
    return this.commonService.getCountryName(code);
  }

  getTags(types: ILocationType[]): InputSelectOption[] {
    return types.map((type) => ({
      label: type.type,
      icon: type.pointOfOrigin ? "target" : null,
    }));
  }

  calculatedRisk = computed(() => {
    const report = this.report();

    if (!report?.calculatedRisk) {
      return null;
    }

    return this.riskLevelsOptionsMap().get(this.report()?.calculatedRisk.title);
  });

  residualRisk = computed(() => {
    if (!this.report().residualRisk) {
      return null;
    }

    return this.riskLevelsOptionsMap().get(this.report().residualRisk.title);
  });

  getSafeHtml(html: string) {
    return this.sanitizer.sanitize(SecurityContext.HTML, html);
  }

  private createRecordGetter<T>(
    service: GraphQLService<T>,
    includes: string[] = ["CUSTOM_FIELDS"],
  ) {
    return {
      get: async (id: string): Promise<T> => {
        const records = await service.getByIdsGraphQL([id], undefined, includes);

        return records[0];
      },
    };
  }

  private async setRecordDetails(uri: string) {
    const match = uri.match(/([^/]+)\/([^/]+)$/);

    if (!match) {
      throw new Error("Invalid record URI");
    }

    const [_, recordType, recordId] = match;

    const apiService = this.mapRecordTypeToApiService[recordType as keyof RecordApiServices];
    const details = await apiService.get(recordId);

    const getRecordName =
      this.recordTypeToNameProperty[recordType as keyof typeof this.recordTypeToNameProperty];

    this.recordNameOrId.set(getRecordName(details));

    const key =
      this.mapRecordTypeToRecordTypeStandard[
        recordType as keyof typeof this.mapRecordTypeToRecordTypeStandard
      ];

    this.recordDetails.set({ [key]: details });
  }

  private readonly riskLevelsOptionsMap = computed(
    () => new Map(this.riskLevelTags().map((level) => [level.title, level])),
  );

  readonly riskLevelTags = computed<Tag[]>(() => this.riskLevels());
}
